import React from 'react';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useSelector } from 'react-redux';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './HowPaymentsWorkPage.module.css';

/**
 * Static page explaining how payments are protected on Nextmine.
 * Linked from the landing-page trust strip and the footer.
 */
const HowPaymentsWorkPage = () => {
  const intl = useIntl();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const title = intl.formatMessage({ id: 'HowPaymentsWorkPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <p className={css.eyebrow}>Nextmine · Secure payments</p>
          <h1 className={css.title}>How payments work</h1>
          <p className={css.meta}>Every sale on Nextmine is protected - for both sides.</p>

          <div className={css.summary}>
            <p className={css.summaryTitle}>At a glance</p>
            <ul>
              <li>
                Your payment is held by Stripe, our secure payment partner - never by the seller,
                and never by Nextmine.
              </li>
              <li>
                The seller is paid only after you confirm your piece has arrived, and after a 14 day
                buyer protection period.
              </li>
              <li>
                If something is wrong, you have 48 hours to report it - and you can get your money
                back.
              </li>
            </ul>
          </div>

          <h2 className={css.h2}>Step by step</h2>

          <h3 className={css.h3}>1. You pay - the money is held in safekeeping</h3>
          <p className={css.p}>
            When you{' '}
            <NamedLink name="SearchPage" className={css.link}>
              buy a piece
            </NamedLink>{' '}
            (or pay for an accepted offer or shipping quote), your payment goes to Stripe, the
            payment provider trusted by millions of businesses worldwide. The seller does not
            receive anything at this point - the money simply waits, safely held, until the sale is
            complete.
          </p>

          <h3 className={css.h3}>2. The seller ships - insured and tracked</h3>
          <p className={css.p}>
            The seller must ship your piece within 7 days, with insured, tracked delivery. You
            receive the tracking number so you can follow your piece all the way to your door. While
            it travels, your money stays on hold.
          </p>

          <h3 className={css.h3}>3. You confirm - then the seller is paid</h3>
          <p className={css.p}>
            When your piece arrives, you confirm it under{' '}
            <NamedLink name="InboxPage" params={{ tab: 'orders' }} className={css.link}>
              Orders &amp; sales
            </NamedLink>{' '}
            ("I've received my order"). That starts a 14 day buyer protection period, and the seller
            is paid at the end of it - never before. If you forget to confirm, the sale completes
            automatically 30 days after the seller marked the piece as shipped, so sellers are
            protected too.
          </p>

          <h2 className={css.h2}>If something is wrong</h2>
          <p className={css.p}>
            If your piece doesn't match the listing, report it within{' '}
            <span className={css.term}>48 hours</span> of confirming delivery. We first ask you and
            the seller to find a solution together - most things are solved with a friendly message.
            If you can't agree within 7 days, Nextmine steps in and makes a decision.
          </p>
          <p className={css.p}>
            If the case is decided in your favour, you return the piece with tracked shipping (you
            pay the return postage), and your money is refunded in full as soon as the seller
            confirms the piece is back. The money never left Stripe's safekeeping - so a refund is
            simple and certain.
          </p>

          <h2 className={css.h2}>Who actually holds the money?</h2>
          <p className={css.p}>
            All payments are processed and held by <span className={css.term}>Stripe</span>, a
            licensed payment institution. Nextmine never holds your money, and the seller can't
            touch it until you have confirmed your purchase. That's the whole idea: nobody gets paid
            until you're happy.
          </p>

          <p className={css.p}>
            Questions? See our{' '}
            <NamedLink name="FaqPage" className={css.link}>
              FAQ
            </NamedLink>{' '}
            or read the full{' '}
            <NamedLink name="TermsOfServicePage" className={css.link}>
              Terms of Service
            </NamedLink>
            .
          </p>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default HowPaymentsWorkPage;
